import type React from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import { TestId } from "../../constants/testIds";
import { isOrderApproved, type OrderStatus } from "../../types/order";
import { formatRupiah } from "../../utils/formatRupiah";

type TranslationKeys = keyof typeof translations.en;

interface OrderTotalsSummaryProps {
  order: {
    _id: string;
    status: OrderStatus;
    expenses?: Array<{ amount?: number }>;
    totalApprovedCost: number;
  };
}

const TotalRow: React.FC<{
  label: string;
  value: React.ReactNode;
  valueTestId?: string;
}> = ({ label, value, valueTestId }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: 1,
      alignItems: "baseline",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="div"
      data-testid={valueTestId}
      sx={{
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const PendingValue: React.FC<{ label: string }> = ({ label }) => (
  <Typography component="span" color="text.secondary" fontStyle="italic" sx={{ fontWeight: 400 }}>
    {label}
  </Typography>
);

const OrderTotalsSummary: React.FC<OrderTotalsSummaryProps> = ({ order }) => {
  const { t } = useTranslation();
  const totalPaid = (order.expenses ?? []).reduce((sum, line) => sum + (line.amount ?? 0), 0);
  const approved = isOrderApproved(order.status);
  const pendingLabel = t("reports.pending" as TranslationKeys);

  return (
    <Box
      data-testid={TestId.UJR.ORDER_TOTALS(order._id)}
      sx={{
        width: "100%",
        minWidth: 200,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} display="block">
        {t("reports.totals" as TranslationKeys)}
      </Typography>
      <Divider sx={{ my: 0.75 }} />
      <Stack spacing={0.75}>
        <TotalRow
          label={t("reports.orderTotalPaid" as TranslationKeys)}
          value={formatRupiah(totalPaid)}
          valueTestId={TestId.UJR.ORDER_TOTAL_PAID(order._id)}
        />
        <TotalRow
          label={t("reports.totalApproved" as TranslationKeys)}
          value={
            approved ? formatRupiah(order.totalApprovedCost) : <PendingValue label={pendingLabel} />
          }
          valueTestId={TestId.UJR.ORDER_TOTAL_APPROVED(order._id)}
        />
      </Stack>
    </Box>
  );
};

export default OrderTotalsSummary;
